import {
  FabricEditor,
  fillGradientPlugin,
  fontLoadingPlugin,
  gridPlugin,
  layerPlugin,
  rectCornersPlugin,
  selectionPlugin,
} from 'modules/fabric-editor';
import { layoutBlocksPlugin } from 'modules/manager/domain/layout-blocks';
import { placeholderPlugin } from 'modules/manager/domain/placeholder';
import React from 'react';
import useIsLocal from 'shared/hooks/use-is-local';

/** Plugin set for headless (off-screen) render pages — shared by the match and standings renderers. */
export const RENDER_PLUGINS = [
  selectionPlugin(),
  layerPlugin(),
  gridPlugin({ visible: false }),
  rectCornersPlugin(),
  fillGradientPlugin(),
  fontLoadingPlugin(),
  placeholderPlugin(),
  layoutBlocksPlugin(),
];

export type RenderStatus = 'loading' | 'rendering' | 'done' | 'error';

/** One persisted image, as shown by the local-only link list under the status text. */
export interface RenderedImageLink {
  url: string;
  label: string;
}

export const StatusOverlay: React.FC<{
  status: string;
  message: string | null;
  title: (status: string) => string;
  links?: RenderedImageLink[];
}> = ({ status, message, title, links }) => {
  const isLocal = useIsLocal();

  return (
    <div className="flex items-center justify-center w-screen h-screen text-gray-600">
      <div className="text-center">
        <div className="text-xl font-bold mb-2">{title(status)}</div>
        {message && <div className="text-sm">{message}</div>}
        {isLocal && links && links.length > 0 && <RenderedImageLinks links={links} />}
      </div>
    </div>
  );
};

/** Debug aid for local renders — the render pages otherwise leave no trace of what they produced
 * short of digging the row out of Supabase by hand. Kept off every deployed build via useIsLocal. */
const RenderedImageLinks: React.FC<{ links: RenderedImageLink[] }> = ({ links }) => (
  <div className="mt-6 mx-auto max-w-2xl text-left border border-gray-300 rounded-lg p-4">
    <div className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-3">
      Rendered images · local only
    </div>
    <ul className="flex flex-col gap-3">
      {links.map((link) => (
        <li key={link.url}>
          <a
            href={link.url}
            target="_blank"
            rel="noreferrer"
            className="block rounded-md px-3 py-2 hover:bg-gray-100"
          >
            <span className="block text-sm font-bold text-gray-700">{link.label}</span>
            <span className="block text-xs font-normal font-mono text-blue-600 break-all">{link.url}</span>
          </a>
        </li>
      ))}
    </ul>
  </div>
);

/**
 * Headless render pages have no other UI to fall back on — a synchronous throw during
 * editor/plugin mount (outside the page's own async try/catch) would otherwise unmount the
 * whole React tree with no error boundary anywhere above it, leaving a blank page. This is the
 * last line of defense so a crash still renders a centered, readable message instead of nothing.
 */
export class RenderErrorBoundary extends React.Component<{ children: React.ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('Render page crashed:', error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <StatusOverlay
          status="error"
          message={this.state.error.message || String(this.state.error)}
          title={() => 'Render failed'}
        />
      );
    }
    return this.props.children;
  }
}

export function parseId(raw: string | null | undefined): number | null {
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function collectBgUrls(params: URLSearchParams): string[] {
  const out: string[] = [];
  for (let i = 1; i <= 50; i++) {
    const value = params.get(`bg${i}`);
    if (value === null) {
      if (i === 1) continue;
      break;
    }
    out.push(value);
  }
  return out;
}

export function loadCanvasJson(editor: FabricEditor, version: unknown, objects: unknown[]): Promise<void> {
  return new Promise((resolve) => {
    editor.canvas.loadFromJSON({ version, objects }, () => {
      editor.canvas.renderAll();
      editor.emit('canvas:loaded', { source: 'render' });
      resolve();
    });
  });
}

export function nextFrame(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => resolve());
    } else {
      setTimeout(resolve, 16);
    }
  });
}
